/**
 * PlanPhaseAccordion — Shared accordion view for plan phases and steps.
 *
 * Works with the real Plan GraphQL type (PlanPhase + PlanStep).
 * Used by PublicPortalPage and can be reused anywhere a read-only
 * phase/step accordion is needed.
 */

import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import BlockIcon from '@mui/icons-material/Block';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { type Plan, type PlanStep, StepStatus } from '../types/generated/graphql';
import { colors } from '../theme/colors';

// ─── Step status icon ────────────────────────────────────────────────────────

function StepIcon({ status }: { status: StepStatus }) {
  switch (status) {
    case StepStatus.Completed:
      return <CheckCircleIcon sx={{ fontSize: 18, color: colors.green[600] }} />;
    case StepStatus.InProgress:
      return <HourglassBottomIcon sx={{ fontSize: 18, color: colors.blue[600] }} />;
    case StepStatus.WaitingExternal:
      return <HourglassBottomIcon sx={{ fontSize: 18, color: colors.amber[600] }} />;
    case StepStatus.Blocked:
      return <BlockIcon sx={{ fontSize: 18, color: colors.red[600] }} />;
    case StepStatus.Skipped:
      return <SkipNextIcon sx={{ fontSize: 18, color: colors.gray[400] }} />;
    default:
      return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: colors.gray[400] }} />;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

interface PlanPhaseAccordionProps {
  plan: Plan;
  /** Index of the phase to expand by default (defaults to 0 = first phase) */
  defaultExpandedIndex?: number;
  /** Optional role lookup map (roleId -> role name) for displaying assigned role */
  showRoles?: boolean;
}

export const PlanPhaseAccordion: React.FC<PlanPhaseAccordionProps> = ({
  plan,
  defaultExpandedIndex = 0,
  showRoles = false,
}) => {
  const sortedPhases = useMemo(
    () => [...plan.phases].sort((a, b) => a.order - b.order),
    [plan.phases],
  );

  const stepsByPhase = useMemo(() => {
    const map = new Map<string, PlanStep[]>();
    sortedPhases.forEach((p) => map.set(p.id, []));
    plan.steps.forEach((s) => {
      const arr = map.get(s.phaseId);
      if (arr) arr.push(s);
    });
    return map;
  }, [plan.steps, sortedPhases]);

  const roleMap = useMemo(() => {
    const m = new Map<string, string>();
    (plan.roles || []).forEach((r) => m.set(r.roleId, r.name));
    return m;
  }, [plan.roles]);

  return (
    <Box>
      {sortedPhases.map((phase, idx) => {
        const steps = stepsByPhase.get(phase.id) || [];
        const done = steps.filter((s) => s.status === StepStatus.Completed).length;

        return (
          <Accordion
            key={phase.id}
            defaultExpanded={idx === defaultExpandedIndex}
            disableGutters
            sx={{
              mb: 1,
              '&:before': { display: 'none' },
              boxShadow: 1,
              borderRadius: '8px !important',
              overflow: 'hidden',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  width: '100%',
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {phase.title}
                </Typography>
                <Chip
                  label={`${done}/${steps.length}`}
                  size="small"
                  sx={{ bgcolor: 'action.hover' }}
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {phase.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {phase.description}
                </Typography>
              )}
              <Stack spacing={0.5}>
                {steps.map((step) => (
                  <Box
                    key={step.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      px: 2,
                      borderRadius: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <StepIcon status={step.status} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{step.title}</Typography>
                      {step.description && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {step.description}
                        </Typography>
                      )}
                    </Box>
                    {showRoles && step.assignedRoleId && roleMap.get(step.assignedRoleId) && (
                      <Chip
                        label={roleMap.get(step.assignedRoleId)}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default PlanPhaseAccordion;
